import { PanelItem } from '../panel-item.class';
import { PanelItemCorners } from '../types';
import { Vertex3 } from '../../../spatial-classes';

export interface GetCornerCfgOptions {
	// @param inside use cutout when true, otherwise overall size
	inside?: boolean;
	// @param includeOverhangs include overhangs when calculating corners
	includeOverhangs?: boolean;
	// @param applyInstanceRotation rotate corners by instanceRotation (for cutout shapes)
	applyInstanceRotation?: boolean;
}

/**
 * Class function to calculate points for each side
 * @param panelItem class instance of PanelItem
 * @param options configuration options
 * @returns PanelItemCorners type
 */
// eslint-disable-next-line max-statements
export const getCornersCfn = (panelItem: PanelItem, options: GetCornerCfgOptions): PanelItemCorners => {
	const { inside = false, includeOverhangs = false, applyInstanceRotation = false } = options;

	const size = inside ? panelItem.cutout : panelItem.size;
	const halfHeight = size.height / 2;
	const halfWidth = size.width / 2;
	const halfDepth = size.depth / 2;

	// include the overhang in the corners if includeOverhangs is true
	const overhang = includeOverhangs ? panelItem.refMap?.panel?.countertopOverhang ?? 0 : 0;

	// distance front of face is the max of the overhang and the distance front of face
	// this ensures that corners for cutout will remove the full countertop overhang
	const distanceFrontOfFace = includeOverhangs && panelItem.item.distanceFrontOfFace !== null
		? Math.max(overhang, panelItem.item.distanceFrontOfFace ?? 0)
		: (panelItem.item.distanceFrontOfFace ?? 0);

	// this is used to adjust the corners for the cutout, offsetting the cutout depth based on full depth and distance front of face
	const rearOverlap = inside ? (panelItem.size.depth - (panelItem.item.distanceFrontOfFace ?? 0)) - panelItem.cutout.depth : 0;

	// if we are looking for inside and the item has a distanceFrontOfFace and distanceAboveCountertop (front and top appliance),
	// then we need to get the outside corners as ears
	const outsideCorners = inside && distanceFrontOfFace > 0 && panelItem.item.distanceBelowCountertop > 0 ? panelItem.getCorners(false) : null;

	// this is used to adjust the center for the rear overlap and distance front of face
	const zCenterAdjustment = inside
		? (rearOverlap / 2)
		: (panelItem.item.distanceFrontOfFace / 2);

	/* Top and Bottom */
	let top = 0;
	let bottom = 0;

	if (panelItem.isFullHeightAppliance && inside) {
		top = -(panelItem.refMap?.panel?.size.height ?? 0) / 2;
	} else {
		top = -halfHeight;
	}
	bottom = -top;

	/* Left and Right */
	const left = -halfWidth;
	const right = halfWidth;

	/* Front and Back */
	// const front = zCenterAdjustment + halfDepth - (!inside && includeOverhangs ? distanceFrontOfFaceDelta : 0);
	const front = zCenterAdjustment + halfDepth + (inside ? distanceFrontOfFace : 0);
	const back = zCenterAdjustment - halfDepth;

	/* Assemble Views */
	const frontView = [
		new Vertex3({ dataIn: { x: left, y: top, z: front }}),
		new Vertex3({ dataIn: { x: right, y: top, z: front }}),
		new Vertex3({ dataIn: { x: right, y: bottom, z: front }}),
		new Vertex3({ dataIn: { x: left, y: bottom, z: front }})
	];

	const backView = [
		new Vertex3({ dataIn: { x: right, y: top, z: back }}),
		new Vertex3({ dataIn: { x: left, y: top, z: back }}),
		new Vertex3({ dataIn: { x: left, y: bottom, z: back }}),
		new Vertex3({ dataIn: { x: right, y: bottom, z: back }})
	];

	const leftView = [
		new Vertex3({ dataIn: { x: back, y: top, z: left }}),
		new Vertex3({ dataIn: { x: front, y: top, z: left }}),
		new Vertex3({ dataIn: { x: front, y: bottom, z: left }}),
		new Vertex3({ dataIn: { x: back, y: bottom, z: left }})
	];

	const rightView = [
		new Vertex3({ dataIn: { x: front, y: top, z: right }}),
		new Vertex3({ dataIn: { x: back, y: top, z: right }}),
		new Vertex3({ dataIn: { x: back, y: bottom, z: right }}),
		new Vertex3({ dataIn: { x: front, y: bottom, z: right }})
	];

	let topView = [];
	let bottomView = [];
	if (outsideCorners) {
		const outsideDelta = (panelItem.size.width / 2) - halfWidth;
		topView = [
			new Vertex3({ dataIn: { x: left, y: back, z: top }}),
			new Vertex3({ dataIn: { x: right, y: back, z: top }}),
			new Vertex3({ dataIn: { x: right, y: front - distanceFrontOfFace, z: top }}),
			new Vertex3({ dataIn: { x: right + outsideDelta, y: front - distanceFrontOfFace, z: top }}),
			new Vertex3({ dataIn: { x: right + outsideDelta, y: front, z: top }}),
			new Vertex3({ dataIn: { x: left - outsideDelta, y: front, z: top }}),
			new Vertex3({ dataIn: { x: left - outsideDelta, y: front - distanceFrontOfFace, z: top }}),
			new Vertex3({ dataIn: { x: left, y: front - distanceFrontOfFace, z: top }})
		];

		bottomView = [
			new Vertex3({ dataIn: { x: left - outsideDelta, y: front, z: bottom }}),
			new Vertex3({ dataIn: { x: right + outsideDelta, y: front, z: bottom }}),
			new Vertex3({ dataIn: { x: right + outsideDelta, y: front - distanceFrontOfFace, z: bottom }}),
			new Vertex3({ dataIn: { x: right, y: front - distanceFrontOfFace, z: bottom }}),
			new Vertex3({ dataIn: { x: right, y: back, z: bottom }}),
			new Vertex3({ dataIn: { x: left, y: back, z: bottom }}),
			new Vertex3({ dataIn: { x: left, y: front - distanceFrontOfFace, z: bottom }}),
			new Vertex3({ dataIn: { x: left - outsideDelta, y: front - distanceFrontOfFace, z: bottom }})
		];
		// console.log('topView', panelItem.item.sku, topView);
	} else {
		topView = [
			new Vertex3({ dataIn: { x: left, y: back, z: top }}),
			new Vertex3({ dataIn: { x: right, y: back, z: top }}),
			new Vertex3({ dataIn: { x: right, y: front, z: top }}),
			new Vertex3({ dataIn: { x: left, y: front, z: top }})
		];

		bottomView = [
			new Vertex3({ dataIn: { x: left, y: front, z: bottom }}),
			new Vertex3({ dataIn: { x: right, y: front, z: bottom }}),
			new Vertex3({ dataIn: { x: right, y: back, z: bottom }}),
			new Vertex3({ dataIn: { x: left, y: back, z: bottom }})
		];
	}

	// Optionally rotate all corners by instanceRotation (for cutout shapes that
	// need to match the rotated appliance). Not applied by default to avoid
	// affecting position computations that depend on axis-aligned corners.
	if (applyInstanceRotation) {
		const { x: rx, y: ry, z: rz } = panelItem.instanceRotation;
		if (rx !== 0 || ry !== 0 || rz !== 0) {
			const rotateVertex = (v: Vertex3): Vertex3 => {
				let { x, y, z } = v;
				if (ry !== 0) {
					const cosY = Math.cos(ry);
					const sinY = Math.sin(ry);
					const nx = x * cosY + z * sinY;
					const nz = -x * sinY + z * cosY;
					x = nx; z = nz;
				}
				if (rz !== 0) {
					const cosZ = Math.cos(rz);
					const sinZ = Math.sin(rz);
					const nx = x * cosZ - y * sinZ;
					const ny = x * sinZ + y * cosZ;
					x = nx; y = ny;
				}
				if (rx !== 0) {
					const cosX = Math.cos(rx);
					const sinX = Math.sin(rx);
					const ny = y * cosX - z * sinX;
					const nz = y * sinX + z * cosX;
					y = ny; z = nz;
				}
				return new Vertex3({ dataIn: { x, y, z } });
			};
			const rot = (view: Array<Vertex3>): Array<Vertex3> => view.map(rotateVertex);
			return {
				front: rot(frontView), back: rot(backView),
				left: rot(leftView), right: rot(rightView),
				top: rot(topView), bottom: rot(bottomView)
			};
		}
	}

	return {
		front: frontView, back: backView,
		left: leftView, right: rightView,
		top: topView, bottom: bottomView
	};
}
